/**
 * Social Media Integration Types
 * Comprehensive type definitions for social sharing, testimonials, and referrals
 */

// Platform types for social media sharing
export type Platform = 'facebook' | 'twitter' | 'instagram' | 'linkedin' | 'whatsapp' | 'copy';

// Extended platform enum for backward compatibility
export enum SocialPlatformExtended {
  Facebook = 'facebook',
  Twitter = 'twitter',
  Instagram = 'instagram',
  LinkedIn = 'linkedin',
  WhatsApp = 'whatsapp',
  Copy = 'copy',
}

// Alias for backward compatibility
export { SocialPlatformExtended as SocialPlatform };

// Social share record
export interface SocialShare {
  id: number;
  userId: string;
  barbershopId: number;
  bookingId?: number;
  platform: Platform;
  content: string;
  imageUrl?: string;
  shareUrl: string;
  shareCount: number;
  createdAt: string;
}

// Request to create a social share
export interface CreateSocialShareRequest {
  bookingId: number;
  platform: Platform;
  customMessage?: string;
}

// Legacy interface for backwards compatibility (has different fields)
export interface CreateSocialShare {
  platform: Platform | SocialPlatformExtended;
  content: string;
  bookingId?: number;
  imageUrl?: string;
}

// Testimonial from a customer
export interface Testimonial {
  id: number;
  customerId: string;
  customerName: string;
  customerAvatar?: string;
  barbershopId: number;
  barbershopName?: string;
  barberId?: number;
  barberName?: string;
  rating: number;
  text: string;
  photos: string[];
  isApproved: boolean;
  shareOnSocial: boolean;
  platform?: Platform;
  createdAt: string;
  updatedAt?: string;
}

// Request to create a testimonial
export interface CreateTestimonialRequest {
  barbershopId: number;
  barberId?: number;
  rating: number;
  text: string;
  photos?: string[];
  shareOnSocial: boolean;
  platform?: Platform;
}

// Legacy type alias for backwards compatibility
export type CreateTestimonial = CreateTestimonialRequest;

// Referral status type
export type ReferralStatus = 'pending' | 'completed' | 'rewarded' | 'expired';

// Referral record
export interface Referral {
  id: number;
  referrerId: string;
  referrerName: string;
  refereeId?: string;
  refereeName?: string;
  refereeEmail?: string;
  platform?: Platform;
  status: ReferralStatus;
  rewardAmount: number;
  referralCode: string;
  createdAt: string;
  completedAt?: string;
  rewardedAt?: string;
}

// Referral statistics
/**
 * Response from GET /social/referrals/my-code.
 * The QR PNG is rendered by the API (previously the client fetched it from
 * api.qrserver.com, which sent every user's referral link to a third party).
 */
export interface ReferralCodeResponse {
  referralCode: string;
  /** Absolute link to /register carrying the code as ?ref=. */
  shareUrl: string;
  /** PNG data URI encoding shareUrl. */
  qrCodeImageBase64: string;
}

export interface ReferralStats {
  totalReferred: number;
  successfulSignups: number;
  pendingSignups: number;
  totalRewardsEarned: number;
  referralCode: string;
  referralUrl?: string;
  conversionRate: number;
}

// Share preview data
export interface SharePreview {
  previewText: string;
  previewImage?: string;
  platforms: Platform[];
  shareUrl: string;
  hashtags?: string[];
}

// Social share statistics by platform
export interface PlatformShareStats {
  platform: Platform;
  shareCount: number;
  clickCount: number;
  conversionCount: number;
  percentage: number;
}

// Share statistics for a barbershop
export interface ShareStats {
  totalShares: number;
  totalClicks: number;
  totalConversions: number;
  byPlatform: PlatformShareStats[];
  shareTrend: ShareTrendItem[];
}

// Share trend over time
export interface ShareTrendItem {
  date: string;
  shares: number;
  clicks: number;
  conversions: number;
}

// Testimonial filter options
export interface TestimonialFilters {
  barbershopId?: number;
  rating?: number;
  sortBy?: 'recent' | 'highest_rated' | 'most_helpful';
  page?: number;
  pageSize?: number;
}

// Paginated testimonial response
export interface TestimonialListResponse {
  testimonials: Testimonial[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  averageRating: number;
}

// Rejection reason for testimonials
export interface RejectTestimonialRequest {
  reason: string;
}

// Track referral request
export interface TrackReferralRequest {
  code: string;
  platform?: Platform;
}

// Platform configuration for UI
export interface PlatformConfig {
  id: Platform;
  name: string;
  icon: string;
  color: string;
  hoverColor: string;
  bgColor: string;
  shareUrlTemplate?: string;
  characterLimit?: number;
}

// Social media share button configuration
export const PLATFORM_CONFIGS: Record<Platform, PlatformConfig> = {
  facebook: {
    id: 'facebook',
    name: 'Facebook',
    icon: 'facebook',
    color: 'text-blue-600',
    hoverColor: 'hover:bg-blue-50 dark:hover:bg-blue-900/20',
    bgColor: 'bg-blue-600 hover:bg-blue-700',
    shareUrlTemplate: 'https://www.facebook.com/sharer/sharer.php?u={url}&quote={text}',
  },
  twitter: {
    id: 'twitter',
    name: 'X (Twitter)',
    icon: 'twitter',
    color: 'text-sky-500',
    hoverColor: 'hover:bg-sky-50 dark:hover:bg-sky-900/20',
    bgColor: 'bg-black hover:bg-gray-800',
    shareUrlTemplate: 'https://twitter.com/intent/tweet?text={text}&url={url}',
    characterLimit: 280,
  },
  instagram: {
    id: 'instagram',
    name: 'Instagram',
    icon: 'instagram',
    color: 'text-pink-600',
    hoverColor: 'hover:bg-pink-50 dark:hover:bg-pink-900/20',
    bgColor: 'bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 hover:opacity-90',
  },
  linkedin: {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: 'linkedin',
    color: 'text-blue-700',
    hoverColor: 'hover:bg-blue-50 dark:hover:bg-blue-900/20',
    bgColor: 'bg-blue-700 hover:bg-blue-800',
    shareUrlTemplate: 'https://www.linkedin.com/sharing/share-offsite/?url={url}',
  },
  whatsapp: {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: 'whatsapp',
    color: 'text-green-600',
    hoverColor: 'hover:bg-green-50 dark:hover:bg-green-900/20',
    bgColor: 'bg-green-600 hover:bg-green-700',
    shareUrlTemplate: 'https://wa.me/?text={text}%20{url}',
  },
  copy: {
    id: 'copy',
    name: 'Copy Link',
    icon: 'link',
    color: 'text-gray-600',
    hoverColor: 'hover:bg-gray-50 dark:hover:bg-gray-700',
    bgColor: 'bg-gray-600 hover:bg-gray-700',
  },
} as const;
